/* eslint-disable @typescript-eslint/naming-convention */
import { Button, H3, Link } from '@ouster/webviz-react-components';

import styles from './Organization.module.css';
import type { OrgSpaceUsage } from '../../types';
import { formatBytesToGB, getUsageColor } from '../../utils/misc';
import { buildTalkToSalesLink } from '../../utils/talkToSales';
import BarChart from '../charts/BarChart';

type OrganizationSpaceUsageProps = {
  orgSpaceUsage: OrgSpaceUsage;
};

export default function OrganizationSpaceUsage({
  orgSpaceUsage,
}: OrganizationSpaceUsageProps) {
  const { space_used, allowed_space: hardSpaceLimit } = orgSpaceUsage;
  const allowed_space = hardSpaceLimit / 2;

  const current = formatBytesToGB(Math.min(space_used, allowed_space));
  const total = formatBytesToGB(allowed_space);
  /* 
    when we fail to get space info, show usage 100
    from release 6, change SOFT_STORAGE_LIMIT be half of the allowed_space
  */
  const used =
    allowed_space === 0 ? 100 : Math.round((space_used / allowed_space) * 100);
  const barColor = getUsageColor(used);

  const tooltipContent = allowed_space === 0 ? 'N/A' : `${current} of ${total}`;

  return (
    <div
      className={styles['section-container']}
      data-testid="org-space-section"
    >
      <H3
        fontSize={16}
        textTransform="uppercase"
        className={styles.heading}
        data-testid="org-space-heading"
      >
        Organization Space Utilization
      </H3>

      <div
        className={styles['space-usage-content']}
        data-testid="org-space-content"
      >
        <BarChart
          current={space_used}
          total={allowed_space}
          tooltipContent={tooltipContent}
          filledColor={barColor}
          tooltipVisibility={space_used > 0 || allowed_space === 0}
          data-testid="org-space-barchart"
        />
        <Button
          variant="outline"
          minWidth={150}
          as={Link}
          href={buildTalkToSalesLink('Organization Space Upgrade')}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="org-space-get-more-btn"
        >
          Get more storage
        </Button>
      </div>
    </div>
  );
}
